<template>
  <v-form ref="form" v-model="formValid" lazy-validation>
    <div class="subtitle1 mb-4">{{ $t("network.initialSetup.selectRegionTitle") }}</div>
    <v-alert
      v-if="!hideDisclaimer"
      :closable="false"
      icon="$warning"
      class="py-0 mb-4 warning-block"
    >
      <span
        class="body2"
        v-html="
          $t('network.secureWebGateway.customDnsFiltering.virtualOfficeSetupDisclaimer', {
            link: networkSettingsPath,
          })
        "
      ></span>
    </v-alert>
    <v-alert
      v-if="hideDisclaimer"
      :closable="false"
      icon="$warning"
      class="py-0 mb-4 warning-block"
    >
      <span class="body2">{{ $t("network.initialSetup.selectRegionDisclaimer") }}</span>
    </v-alert>
    <div class="mb-4">
      <v-autocomplete
        v-model="localValue.region"
        :items="sortedRegions"
        menu-icon="icon-arrow-down-xs"
        item-title="displayValue"
        item-value="value"
        :rules="[requiredRule]"
        :label="$t('network.initialSetup.selectRegionLabel')"
        :placeholder="$t('network.initialSetup.selectRegionPlaceholder')"
        variant="outlined"
        background-color="white"
        return-object
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props" title="">
            <span class="ml-1">{{ item.raw.displayValue }}</span>
          </v-list-item>
        </template>
      </v-autocomplete>
      <v-select
        v-model="localValue.subnet"
        :items="subnetOptions"
        menu-icon="icon-arrow-down-xs"
        :rules="[required()]"
        :label="$t('network.initialSetup.selectSubnetLabel')"
        :hint="$t('network.initialSetup.defaultSubnetHint')"
        persistent-hint
        variant="outlined"
        class="mt-2"
      >
        <template #message="{ message }">
          <div class="d-flex align-center">
            <v-icon size="x-small" icon="$infoXs" class="mr-1" />
            <span>{{ message }}</span>
          </div>
        </template>
      </v-select>
    </div>
    <div class="subtitle1 mb-4">
      {{ $t("modals.setupVirtualOffice.confirmInputLabel") }}
    </div>
    <v-text-field
      v-model="localValue.approveText"
      class="mt-6"
      outlined
      :rules="matchApproveRules"
    ></v-text-field>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { VuetifyFormRef } from "@/types.ts";
import { useI18n } from "vue-i18n";
import { useVirtualOfficeStore } from "@/_store/network/virtual-office.module.ts";
import isEmpty from "lodash/isEmpty";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes.ts";
import {
  DEFAULT_VIRTUAL_OFFICE_SUBNET,
  VIRTUAL_OFFICE_SUBNET_OPTIONS,
} from "@/constants/network.ts";
import { required } from "@/_helpers/validators.ts";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<
        DialogDataConfig<{ region: { value: string; displayValue: string } }>
      >,
      required: true,
    },
    hideDisclaimer: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const localValue = ref<{
      region: { value: string; displayValue: string } | null;
      subnet: string;
      approveText: string;
    }>({ region: null, subnet: DEFAULT_VIRTUAL_OFFICE_SUBNET, approveText: "" });
    const subnetOptions = VIRTUAL_OFFICE_SUBNET_OPTIONS;

    const form = ref<VuetifyFormRef>();
    const { t } = useI18n();
    const router = useRouter();
    const formValid = ref(true);
    const regions = ref<Record<string, string>>({});
    const virtualOfficeStore = useVirtualOfficeStore();
    const networkSettingsPath = computed(
      () => router.resolve({ name: RouteName.NETWORK_SETTINGS_GENERAL }).href
    );
    const sortedRegions = computed(() => {
      return Object.entries(regions.value)
        .map(([value, displayValue]) => ({
          value,
          displayValue,
        }))
        .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    });
    const matchApproveRules = [
      (v?: string) => {
        if (!v || !v.length) {
          return t("validations.typeApprove.required");
        }
        if (v !== t("validations.typeApprove.approve").toUpperCase()) {
          return t("validations.typeApprove.exactMatch");
        }
        return true;
      },
    ];
    const requiredRule = (v: { value: string; displayValue: string } | null) =>
      isEmpty(v) ? t("validations.required") : true;

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true }
    );

    watch(formValid, (value) => {
      if (value === null) {
        return;
      }

      emit("update:valid", value);
    });

    onMounted(async () => {
      try {
        regions.value = await virtualOfficeStore.getVpnRegions();
      } catch (error) {
        console.error(error);
      }
    });

    const validate = async () => {
      const validationResult = await form.value?.validate();

      return validationResult?.valid;
    };

    return {
      required,
      requiredRule,
      subnetOptions,
      sortedRegions,
      formValid,
      localValue,
      form,
      matchApproveRules,
      validate,
      networkSettingsPath,
    };
  },
});
</script>

<style scoped lang="scss"></style>
